import LegalLayout, { P, UL, H3 } from "@/components/site/LegalLayout";
import SITE from "@/lib/site";

export default function PrivacyPolicy() {
  const L = SITE.legal;

  const sections = [
    {
      id: "introduction",
      title: "1. Introduction",
      body: (
        <>
          <P>
            {L.companyName} (“Ambuly,” “we,” “us,” or “our”) operates the Ambuly website,
            mobile application and related services (collectively, the “Service”), which
            provide on-demand and scheduled ambulance booking, dispatch, and related
            healthcare logistics.
          </P>
          <P>
            This Privacy Policy explains how we collect, use, disclose, and safeguard your
            information when you use the Service. It applies to all users, including
            patients, family members, healthcare professionals, partners and website
            visitors.
          </P>
          <P>
            Please read this Policy carefully. By accessing or using the Service, you
            acknowledge that you have read and understood the practices described herein.
            If you do not agree with the terms of this Privacy Policy, please do not use
            the Service.
          </P>
        </>
      ),
    },
    {
      id: "information-we-collect",
      title: "2. Information We Collect",
      body: (
        <>
          <H3>2.1 Information you provide directly</H3>
          <UL
            items={[
              "Account information: name, email, phone number, password, profile photo (optional).",
              "Patient information: age, sex, medical condition, symptoms, allergies, medications, medical history, and other health-related information necessary to dispatch appropriate care.",
              "Booking details: pickup and drop-off location, preferred hospital, service level (BLS/ALS/ICU), scheduled time, and notes to the crew.",
              "Payment information: billing name, billing address, and payment tokens processed by our PCI-DSS-compliant payment providers (we do not store full card numbers on our servers).",
              "Communications: messages, emails, call recordings (where lawful and disclosed), ratings and feedback you submit.",
              "Identity and verification documents where required for corporate, insurance or partner accounts.",
            ]}
          />
          <H3>2.2 Information we collect automatically</H3>
          <UL
            items={[
              "Device and log data: IP address, device identifiers, operating system, browser type, app version, crash reports, diagnostic data.",
              "Usage data: pages visited, features used, search terms, timestamps, referring pages, clickstream information.",
              "Location data: precise GPS coordinates (only when you book a ride or when you grant location access), cell/Wi-Fi-based location, and route information used to dispatch and track ambulances.",
              "Cookies and similar technologies: session cookies, preference cookies, analytics and security cookies. See Section 9 for details and choices.",
            ]}
          />
          <H3>2.3 Information from third parties</H3>
          <UL
            items={[
              "Hospitals, clinics, insurance partners and employers who book a ride on your behalf or as part of a coverage plan.",
              "Identity verification providers, fraud-prevention services and background-check providers.",
              "Emergency contacts you designate, and paramedics/operators during a dispatched trip.",
              "Analytics and advertising partners, where permitted by your preferences and applicable law.",
            ]}
          />
          <P>
            Some information (e.g., health information) is sensitive. We collect sensitive
            information only where necessary to provide the Service, with your consent
            where required, and protect it with appropriate safeguards.
          </P>
        </>
      ),
    },
    {
      id: "how-we-use",
      title: "3. How We Use Your Information",
      body: (
        <>
          <P>We use your information to:</P>
          <UL
            items={[
              "Provide, maintain, and operate the Service, including booking, dispatch, live tracking, in-app communication, billing, and receipts.",
              "Match you with the most appropriate ambulance, crew and equipment, and enable safe clinical handover to hospitals.",
              "Verify identity, prevent fraud, enforce our Terms of Service, and protect the rights, property, and safety of Ambuly, our users, and the public.",
              "Process payments and refunds, reconcile insurance or corporate billing, and comply with financial record-keeping obligations.",
              "Communicate with you regarding bookings, service updates, customer support, safety alerts and, where permitted, marketing.",
              "Analyse and improve the Service, research clinical and operational outcomes (using aggregated or de-identified data), and develop new features.",
              "Comply with applicable laws, regulations, court orders, and lawful government requests.",
            ]}
          />
        </>
      ),
    },
    {
      id: "legal-bases",
      title: "4. Legal Bases for Processing",
      body: (
        <>
          <P>
            Where required by law (e.g., GDPR, UK GDPR, or similar regulations), we process
            your personal data only where we have a lawful basis to do so:
          </P>
          <UL
            items={[
              "Contract: to perform the service you have requested (e.g., to book and dispatch an ambulance).",
              "Consent: where you have provided consent — for example, to process sensitive health data or to send you marketing messages. You can withdraw your consent at any time.",
              "Legal obligation: to comply with laws applicable to healthcare, taxation, accounting, anti-money-laundering, and emergency response.",
              "Legitimate interests: to secure the Service, prevent fraud, improve the Service, and conduct business operations — balanced against your rights.",
              "Vital interests: to protect life during a medical emergency.",
            ]}
          />
        </>
      ),
    },
    {
      id: "how-we-share",
      title: "5. How We Share Your Information",
      body: (
        <>
          <P>
            We do not sell your personal information. We share information only as
            described below:
          </P>
          <H3>5.1 Service partners</H3>
          <UL
            items={[
              "Ambulance operators, drivers, paramedics and hospitals necessary to dispatch and complete your booking.",
              "Payment processors, billing platforms, and insurance partners required to process your payment or claim.",
              "Technology service providers: cloud hosting, communications, mapping, analytics, fraud-prevention, identity verification, and customer support tooling — all bound by written contracts and confidentiality obligations.",
            ]}
          />
          <H3>5.2 Legal, safety, and compliance</H3>
          <UL
            items={[
              "To comply with applicable law, subpoenas, court orders, or lawful government requests.",
              "To enforce our Terms of Service, protect the rights, property, or safety of Ambuly, our users, or others.",
              "To respond to a medical emergency where sharing information is necessary to protect a person's vital interests.",
            ]}
          />
          <H3>5.3 Business transfers</H3>
          <P>
            If Ambuly is involved in a merger, acquisition, financing, reorganisation, or
            sale of assets, your information may be transferred as part of that transaction,
            subject to customary confidentiality and continued application of this Policy.
          </P>
          <H3>5.4 With your consent</H3>
          <P>
            We may share information for any other purpose disclosed to you at the time of
            collection or with your explicit consent.
          </P>
        </>
      ),
    },
    {
      id: "international-transfers",
      title: "6. International Data Transfers",
      body: (
        <>
          <P>
            Ambuly operates globally. Your information may be transferred to, stored or
            processed in countries other than your own, including in {L.jurisdiction}.
            When we transfer personal data internationally, we rely on lawful transfer
            mechanisms such as the European Commission's Standard Contractual Clauses,
            adequacy decisions, or your explicit consent where required.
          </P>
        </>
      ),
    },
    {
      id: "data-retention",
      title: "7. Data Retention",
      body: (
        <>
          <P>
            We retain personal information only for as long as necessary to provide the
            Service, comply with legal and regulatory obligations (including healthcare,
            tax and financial record-keeping), resolve disputes, and enforce our agreements.
          </P>
          <UL
            items={[
              "Account data: while your account is active and for a reasonable period after closure.",
              "Trip and clinical records: for the retention period mandated by applicable healthcare regulations (often 5–10 years).",
              "Financial records: typically 7 years, or the period required by local tax law.",
              "Call and communication logs: typically up to 24 months, longer where required for dispute resolution.",
            ]}
          />
          <P>When information is no longer needed, we securely delete or anonymise it.</P>
        </>
      ),
    },
    {
      id: "security",
      title: "8. Data Security",
      body: (
        <>
          <P>
            We implement administrative, technical, and physical safeguards designed to
            protect your information, including:
          </P>
          <UL
            items={[
              "Encryption of data in transit (TLS 1.2+) and at rest (AES-256 or equivalent) for sensitive fields.",
              "Role-based access controls, least-privilege principles, and audit logging.",
              "Background checks and privacy training for personnel with access to patient data.",
              "Vulnerability management, intrusion detection, and independent security assessments.",
              "Incident response and breach-notification procedures aligned with applicable laws.",
            ]}
          />
          <P>
            No method of transmission or storage is 100% secure. While we strive to use
            commercially acceptable means to protect your information, we cannot guarantee
            its absolute security.
          </P>
        </>
      ),
    },
    {
      id: "cookies",
      title: "9. Cookies & Similar Technologies",
      body: (
        <>
          <P>
            We use cookies, SDKs, and similar technologies to operate the Service, remember
            your preferences, secure your session, measure performance, and, where
            permitted, support advertising.
          </P>
          <UL
            items={[
              "Strictly necessary cookies — required for login, security and core functions.",
              "Functional cookies — remember your preferences (language, region).",
              "Analytics cookies — help us understand usage to improve the Service.",
              "Marketing cookies — used only with your consent where required by law.",
            ]}
          />
          <P>
            You can control cookies through your browser settings and, where available, our
            in-product cookie preferences. Blocking some cookies may impact Service
            functionality.
          </P>
        </>
      ),
    },
    {
      id: "your-rights",
      title: "10. Your Rights & Choices",
      body: (
        <>
          <P>
            Depending on your location, you may have the following rights with respect to
            your personal information:
          </P>
          <UL
            items={[
              "Access: request a copy of the personal information we hold about you.",
              "Rectification: ask us to correct inaccurate or incomplete information.",
              "Erasure: request deletion of personal information, subject to legal retention obligations.",
              "Restriction or objection: ask us to restrict or object to certain processing.",
              "Portability: request a copy of certain data in a structured, machine-readable format.",
              "Withdraw consent: at any time, where processing is based on consent.",
              "Non-discrimination: you will not be discriminated against for exercising your rights.",
            ]}
          />
          <P>
            To exercise any of these rights, contact us at{" "}
            <a className="underline hover:text-[#1A3A32]" href={`mailto:${L.email}`}>{L.email}</a>.
            We will verify your identity and respond within the timeframe required by law.
            You also have the right to lodge a complaint with your local data protection
            authority.
          </P>
          <H3>Region-specific disclosures</H3>
          <P>
            <strong className="text-[#14241F]">California (CCPA/CPRA):</strong> California
            residents have the right to know, delete, correct and limit the use of their
            sensitive personal information. We do not sell personal information, and we do
            not share it for cross-context behavioural advertising without consent.
          </P>
          <P>
            <strong className="text-[#14241F]">European Economic Area / United Kingdom:</strong>{" "}
            You may contact our EU/UK representative at the email above. You have the right
            to lodge a complaint with your local supervisory authority.
          </P>
          <P>
            <strong className="text-[#14241F]">India (DPDP Act):</strong> Data principals may
            request access, correction, erasure, and grievance redressal. Contact our
            Grievance Officer at the email above.
          </P>
        </>
      ),
    },
    {
      id: "children",
      title: "11. Children's Privacy",
      body: (
        <>
          <P>
            The Service is not directed to children under 16 (or the minimum age required
            in your jurisdiction), and we do not knowingly collect personal information
            from them without parental or guardian consent. Where a minor requires
            emergency ambulance services, we rely on parental/guardian consent and our
            vital-interests basis to provide care.
          </P>
          <P>
            If you believe we have collected personal information from a child without
            appropriate consent, please contact us and we will promptly take steps to
            delete such information.
          </P>
        </>
      ),
    },
    {
      id: "third-party-links",
      title: "12. Third-Party Links & Services",
      body: (
        <>
          <P>
            The Service may contain links to third-party websites, apps, or services that
            are not operated by Ambuly. We are not responsible for the privacy practices of
            those third parties. We encourage you to review their privacy policies.
          </P>
        </>
      ),
    },
    {
      id: "mobile-platforms",
      title: "13. Mobile Platform Disclosures (Apple / Google)",
      body: (
        <>
          <P>
            When you use our iOS or Android apps, additional disclosures apply in line with
            Apple App Store Review Guidelines and Google Play Policy:
          </P>
          <UL
            items={[
              "Location: We request precise location only to dispatch an ambulance, navigate, track the trip live, and confirm pickup/drop-off. You may disable location access in your device settings; core features will not work without it.",
              "Microphone / Camera: Used only when you enable features such as uploading documents, scanning prescriptions, or contacting support. We never access the camera or microphone in the background.",
              "Contacts: Accessed only if you choose to add an emergency contact from your address book. We do not upload your full contact list.",
              "Health data: Where the app integrates with Apple HealthKit or Google Health Connect, data is used solely to inform paramedics during an emergency and is never sold or used for advertising.",
              "App Tracking Transparency (iOS): We do not track you across other companies' apps and websites. If that changes, we will request your permission via the ATT prompt.",
              "Data Safety (Google Play): The data types we collect, share and our security practices are disclosed in the Play Store Data Safety section and match this Policy.",
              "Children: Our apps are not directed to children and are not designed to appear in the Kids Category.",
            ]}
          />
        </>
      ),
    },
    {
      id: "automated-decisions",
      title: "14. Automated Decision-Making",
      body: (
        <>
          <P>
            We use automated systems to match ambulances to requests, detect fraud and
            route vehicles efficiently. These systems are reviewed by humans and are not
            used to make decisions that produce legal or similarly significant effects on
            you without human oversight.
          </P>
        </>
      ),
    },
    {
      id: "changes",
      title: "15. Changes to This Privacy Policy",
      body: (
        <>
          <P>
            We may update this Privacy Policy from time to time. When we make material
            changes, we will update the “Last updated” date and, where appropriate, notify
            you via the Service, by email or in-app notice. Your continued use of the
            Service after the effective date of changes constitutes your acceptance of the
            updated Policy.
          </P>
        </>
      ),
    },
    {
      id: "contact",
      title: "16. Contact Us",
      body: (
        <>
          <P>
            If you have questions about this Privacy Policy or our privacy practices, or
            wish to exercise your rights, please contact us:
          </P>
          <div className="rounded-2xl bg-[#EDF1ED] p-6 mt-3">
            <p className="text-[#14241F]"><strong>{L.companyName}</strong></p>
            <p className="text-[#495D56] mt-1">{L.address}</p>
            <p className="text-[#495D56] mt-1">Email: <a className="underline hover:text-[#1A3A32]" href={`mailto:${L.email}`}>{L.email}</a></p>
            <p className="text-[#495D56] mt-1">Jurisdiction: {L.jurisdiction}</p>
          </div>
        </>
      ),
    },
  ];

  return (
    <div data-testid="privacy-policy-page">
      <LegalLayout
        eyebrow="Legal"
        title="Privacy Policy"
        lastUpdated={L.effectiveDate}
        sections={sections}
      >
        <p className="text-[#495D56] text-[17px] leading-relaxed">
          Your privacy is fundamental to how Ambuly operates. This Privacy Policy
          describes, in plain language, what data we collect, how we use it, with whom
          we share it, and the choices and rights you have. It is designed to align
          with leading privacy standards including the EU General Data Protection
          Regulation (GDPR), the California Consumer Privacy Act (CCPA/CPRA), India's
          Digital Personal Data Protection Act (DPDP), HIPAA-style safeguards, and the
          privacy requirements of the Apple App Store and Google Play Store.
        </p>
      </LegalLayout>
    </div>
  );
}
